/**
 * MikroTik PoE-out switch catalog — CRS / CSS / hEX / netPower.
 *
 * All specs verified against mikrotik.com/product/<id> pages on
 * 2026-05-15. URL cited per entry. Missing specs left undefined.
 *
 * Scope rules applied:
 *  - Passive-PoE-out-only models (RB260GSP, netPower Lite 7R, netPower
 *    15FR) skipped — not 802.3af/at compliant.
 *  - Models without a published total PoE budget (CRS112-8P-4S-IN,
 *    netPower 16P, RB960PGS) skipped — the schema requires `poeBudgetW`.
 *  - PoE-IN-only models (CSS610-8G-2S+IN, CRS305, CRS310-1G/8G) skipped.
 *
 * CRS320-8P-8B-4S+RM is mapped to 802.3bt-Type4 (90 W per port) per
 * the spec page; CRS418/CRS328/CRS354 are 802.3at (30 W per port).
 */

import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const MIKROTIK_SWITCH_CATALOG: EquipmentCatalogEntry[] = [
  // CRS328-24P-4S+RM (id: mikrotik-crs328-24p-4s-rm) covered by
  // networkSwitchCatalog.ts — kept there to preserve its richer
  // switchingCapacity / operatingTemp fields.

  {
    id: 'mikrotik-crs354-48p-4s-2q-rm',
    manufacturer: 'MikroTik',
    model: 'CRS354-48P-4S+2Q+RM',
    category: 'poe-switch',
    tagline: 'L3-managed 48-port Gigabit PoE+ with 4× 10G SFP+ and 2× 40G QSFP+',
    verified: true,
    datasheetUrl: 'https://mikrotik.com/product/crs354_48p_4s_2q_rm',
    specs: {
      totalPorts: 54,
      poePorts: 48,
      poeBudgetW: 700,
      poeStandard: '802.3at',
      uplinks: '4× SFP+ 10G + 2× QSFP+ 40G',
      switchingCapacityGbps: 336,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  {
    id: 'mikrotik-crs320-8p-8b-4s-rm',
    manufacturer: 'MikroTik',
    model: 'CRS320-8P-8B-4S+RM',
    category: 'poe-switch',
    tagline: 'L3-managed 16-port Gigabit PoE++ (90 W) with 4× 10G SFP+',
    verified: true,
    datasheetUrl: 'https://mikrotik.com/product/crs320_8p_8b_4s_rm',
    specs: {
      totalPorts: 20,
      poePorts: 16,
      poeBudgetW: 963,
      poeStandard: '802.3bt-Type4',
      poePerPortW: 90,
      uplinks: '4× SFP+ 10G',
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  {
    id: 'mikrotik-crs418-8p-8g-2s-rm',
    manufacturer: 'MikroTik',
    model: 'CRS418-8P-8G-2S+RM',
    category: 'poe-switch',
    tagline: 'L3-managed 16-port Gigabit (8 PoE+) with 2× 10G SFP+ and dual PSU',
    verified: true,
    datasheetUrl: 'https://mikrotik.com/product/crs418_8p_8g_2s_rm',
    specs: {
      totalPorts: 16,
      poePorts: 8,
      poeBudgetW: 150,
      poeStandard: '802.3at',
      uplinks: '2× SFP+ 10G',
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // CSS610-8P-2S+IN (id: mikrotik-css610-8p-2s-in) covered by
  // networkSwitchCatalog.ts.

  {
    id: 'mikrotik-netpower-lite-8p',
    manufacturer: 'MikroTik',
    model: 'netPower Lite 8P',
    category: 'poe-switch',
    tagline: 'SwOS Lite outdoor 8-port Gigabit PoE+ with 2× 10G SFP+, IP54',
    verified: true,
    datasheetUrl: 'https://mikrotik.com/product/netpower_lite_ups',
    specs: {
      totalPorts: 10,
      poePorts: 8,
      poeBudgetW: 120,
      poeStandard: '802.3at',
      uplinks: '2× SFP+ 10G',
      management: 'smart-managed',
      formFactor: 'industrial-DIN',
      fanless: true,
    },
  },

  {
    id: 'mikrotik-rb5009upr-s-in',
    manufacturer: 'MikroTik',
    model: 'RB5009UPr+S+IN',
    category: 'poe-switch',
    tagline: 'L3-managed 8-port Gigabit PoE+ router with 2.5G + 10G SFP+, fanless',
    verified: true,
    datasheetUrl: 'https://mikrotik.com/product/rb5009upr_s_in',
    specs: {
      totalPorts: 9,
      poePorts: 8,
      poeBudgetW: 130,
      poeStandard: '802.3at',
      poePerPortW: 25,
      uplinks: '1× SFP+ 10G',
      management: 'L3-managed',
      formFactor: 'desktop',
      fanless: true,
    },
  },
];
